/* screens/crm/CrmSettings.jsx — конструктор воронок «в одном месте» (только админ).
   Слева — все воронки (+ создать), справа — выбранная: этапы / параметры / приём по
   API. Не зависит от того, какая воронка открыта на доске (выбор — через /:pipelineId).
   Заменяет прежний PipelineSettingsDrawer (drawer над доской). */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { ErrorState, EmptyState } from '../../components/patterns/States.jsx';
import { Tabs } from '../../components/patterns/Tabs.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, NativeSelect, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as crm from '../../lib/api/crm.js';

const { useState, useMemo, useEffect } = React;
const { useFakeQuery } = hooks;
const { useNavigate, useParams } = RR;

function StageRow({ stage, kinds, index, total, onSaved, onReorder, onArchive }) {
  const [name, setName] = useState(stage.name);
  const [kind, setKind] = useState(stage.kind);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(stage.name); setKind(stage.kind); }, [stage.id, stage.name, stage.kind]);
  const dirty = name.trim() !== stage.name || kind !== stage.kind;
  const save = async () => {
    if (!name.trim() || !dirty) return;
    setBusy(true);
    try { await onSaved(stage, { name: name.trim(), kind }); } finally { setBusy(false); }
  };
  return (
    <div className="crm-stage-row">
      <div className="stack" style={{ gap: 2 }}>
        <button type="button" className="icon-btn-mini" aria-label="Выше" disabled={index === 0}
          onClick={() => onReorder(index, index - 1)}><Icon name="chevron-up" size={14} /></button>
        <button type="button" className="icon-btn-mini" aria-label="Ниже" disabled={index === total - 1}
          onClick={() => onReorder(index, index + 1)}><Icon name="chevron-down" size={14} /></button>
      </div>
      <div className="crm-stage-row__name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)}
          onBlur={save} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} invalid={!name.trim()} />
      </div>
      <NativeSelect value={kind} onChange={(e) => setKind(e.target.value)} options={kinds} style={{ width: 130 }} />
      {dirty && <Button size="sm" variant="secondary" loading={busy} onClick={save}>OK</Button>}
      <button type="button" className="icon-btn-mini" aria-label="Архивировать этап"
        title="Архивировать этап" onClick={() => onArchive(stage)}><Icon name="trash" size={15} /></button>
    </div>
  );
}

// Типы динамических полей лида (синхронно с services.CUSTOM_FIELD_TYPES).
const FIELD_TYPES = [
  { value: 'text', label: 'Текст' },
  { value: 'textarea', label: 'Длинный текст' },
  { value: 'number', label: 'Число' },
  { value: 'select', label: 'Список' },
  { value: 'date', label: 'Дата' },
];

function FieldsEditor({ pipeline, onChanged }) {
  const toast = useToast();
  const fromPipeline = () => (pipeline.custom_fields || []).map((d) => ({ ...d }));
  const [defs, setDefs] = useState(fromPipeline);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDefs(fromPipeline()); }, [pipeline.id, pipeline.custom_fields]); // eslint-disable-line

  const dirty = JSON.stringify(defs) !== JSON.stringify(pipeline.custom_fields || []);
  const update = (i, patch) => setDefs((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const add = () => setDefs((ds) => [...ds, { label: '', type: 'text' }]);
  const remove = (i) => setDefs((ds) => ds.filter((_, idx) => idx !== i));
  const move = (i, to) => {
    if (to < 0 || to >= defs.length) return;
    setDefs((ds) => { const a = [...ds]; const [m] = a.splice(i, 1); a.splice(to, 0, m); return a; });
  };

  const save = async () => {
    const payload = defs.map((d) => {
      const e = { label: (d.label || '').trim(), type: d.type || 'text' };
      if (d.key) e.key = d.key;  // сохраняем ключ существующих полей (значения лидов не осиротеют)
      if (e.type === 'select') {
        e.options = (Array.isArray(d.options) ? d.options : [])
          .map((s) => String(s).trim()).filter(Boolean);
      }
      return e;
    });
    if (payload.some((d) => !d.label)) { toast.show('У каждого поля должно быть название.', 'danger'); return; }
    if (payload.some((d) => d.type === 'select' && !(d.options || []).length)) {
      toast.show('У поля-списка укажите хотя бы один вариант.', 'danger'); return;
    }
    setSaving(true);
    try { await crm.updatePipeline(pipeline.id, { custom_fields: payload }); toast.show('Поля сохранены', 'accent'); onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось сохранить поля.', 'danger'); }
    finally { setSaving(false); }
  };

  return (
    <div className="stack stack-12">
      <p className="t-caption text-mute">
        Дополнительные поля лида — свои у каждой воронки. Все необязательны. Стабильные поля
        (клиент, компания, контакты) есть всегда и здесь не настраиваются.
      </p>
      <div className="stack stack-8">
        {defs.length === 0 && <p className="text-dim">Пока нет дополнительных полей.</p>}
        {defs.map((d, i) => (
          <div key={i} className="crm-field-row">
            <div className="stack" style={{ gap: 2 }}>
              <button type="button" className="icon-btn-mini" aria-label="Выше" disabled={i === 0}
                onClick={() => move(i, i - 1)}><Icon name="chevron-up" size={14} /></button>
              <button type="button" className="icon-btn-mini" aria-label="Ниже" disabled={i === defs.length - 1}
                onClick={() => move(i, i + 1)}><Icon name="chevron-down" size={14} /></button>
            </div>
            <div className="grow stack stack-8" style={{ minWidth: 0 }}>
              <div className="cluster cluster-8" style={{ alignItems: 'flex-start' }}>
                <TextInput className="grow" value={d.label} placeholder="Название поля (напр. «Город»)"
                  onChange={(e) => update(i, { label: e.target.value })} />
                <NativeSelect value={d.type || 'text'} options={FIELD_TYPES} style={{ width: 130 }}
                  onChange={(e) => update(i, { type: e.target.value })} />
                <button type="button" className="icon-btn-mini" aria-label="Удалить поле"
                  title="Удалить поле" onClick={() => remove(i)}><Icon name="trash" size={15} /></button>
              </div>
              {d.type === 'select' && (
                <Field label="Варианты (по одному в строке)">
                  <Textarea minRows={2} maxRows={8}
                    value={(d.options || []).join('\n')}
                    onChange={(e) => update(i, { options: e.target.value.split('\n') })}
                    placeholder={'Сайт\nЗвонок\nРекомендация'} />
                </Field>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="cluster cluster-8">
        <Button size="sm" variant="secondary" iconLeft="plus" onClick={add}>Добавить поле</Button>
        <div className="grow" />
        <Button variant="primary" loading={saving} disabled={!dirty} onClick={save}>Сохранить поля</Button>
      </div>
    </div>
  );
}

function FunnelDetail({ pipeline, meta, onChanged, onArchived }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState('stages');
  const kinds = meta?.stage_kinds || [];

  const [pname, setPname] = useState(pipeline.name);
  const [pslug, setPslug] = useState(pipeline.slug);
  const [pdefault, setPdefault] = useState(!!pipeline.is_default);
  const [savingP, setSavingP] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [addingStage, setAddingStage] = useState(false);
  useEffect(() => {
    setPname(pipeline.name); setPslug(pipeline.slug); setPdefault(!!pipeline.is_default); setTab('stages');
  }, [pipeline.id, pipeline.name, pipeline.slug, pipeline.is_default]);

  const stages = pipeline.stages || [];

  const savePipeline = async () => {
    setSavingP(true);
    try {
      await crm.updatePipeline(pipeline.id, { name: pname.trim(), slug: pslug.trim(), is_default: pdefault });
      toast.show('Воронка сохранена', 'accent'); onChanged();
    } catch (e) { toast.show(e?.detail || 'Не удалось сохранить воронку.', 'danger'); }
    finally { setSavingP(false); }
  };
  const saveStage = async (stage, body) => {
    try { await crm.updateStage(stage.id, body); onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось сохранить этап.', 'danger'); }
  };
  const addStage = async () => {
    if (!newStage.trim()) return;
    setAddingStage(true);
    try { await crm.addStage(pipeline.id, { name: newStage.trim(), kind: 'open' }); setNewStage(''); onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось добавить этап.', 'danger'); }
    finally { setAddingStage(false); }
  };
  const reorder = async (from, to) => {
    if (to < 0 || to >= stages.length) return;
    const ids = stages.map((s) => s.id);
    const [moved] = ids.splice(from, 1); ids.splice(to, 0, moved);
    try { await crm.reorderStages(pipeline.id, ids); onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось изменить порядок.', 'danger'); }
  };
  const archiveStage = async (stage) => {
    const ok = await confirm({ title: `Архивировать «${stage.name}»?`,
      body: 'Этап скроется с доски. На этапе не должно быть активных лидов.', confirmLabel: 'Архивировать', tone: 'danger' });
    if (!ok) return;
    try { await crm.archiveStage(stage.id); onChanged(); }
    catch (e) { toast.show(e?.detail || 'Не удалось архивировать этап.', 'danger'); }
  };
  const archivePipeline = async () => {
    const ok = await confirm({ title: `Архивировать воронку «${pipeline.name}»?`,
      body: 'Воронка скроется из списка. Лиды останутся в архиве.', confirmLabel: 'Архивировать', tone: 'danger' });
    if (!ok) return;
    try { await crm.archivePipeline(pipeline.id); toast.show('Воронка в архиве', 'accent'); onArchived(pipeline.id); }
    catch (e) { toast.show(e?.detail || 'Не удалось архивировать воронку.', 'danger'); }
  };

  const ingest = meta?.ingest || {};
  const example = `POST ${ingest.endpoint || '/api/crm/ingest/'}
${ingest.header || 'X-CRM-Ingest-Key'}: ${ingest.key || '<ваш-ключ>'}
Content-Type: application/json

{
  "pipeline": "${pipeline.slug}",
  "title": "Заявка с сайта",
  "company": "ООО Ромашка",
  "email": "info@romashka.ru",
  "source_detail": "лендинг",
  "external_id": "form-42"
}`;
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.show('Скопировано', 'accent'); }
    catch (_) { toast.show('Не удалось скопировать', 'danger'); }
  };

  return (
    <div className="crm-settings__detail stack stack-12">
      <Tabs variant="line" value={tab} onChange={setTab} items={[
        { value: 'stages', label: 'Этапы', count: stages.length },
        { value: 'fields', label: 'Поля', count: (pipeline.custom_fields || []).length },
        { value: 'pipeline', label: 'Параметры' },
        { value: 'api', label: 'Приём по API' },
      ]} />

      {tab === 'fields' && <FieldsEditor pipeline={pipeline} onChanged={onChanged} />}

      {tab === 'stages' && (
        <div className="stack stack-12">
          <p className="t-caption text-mute">Первый этап — точка входа новых лидов. Тип «Успех»/«Отказ» помечает закрытие сделки.</p>
          <div className="stack stack-8">
            {stages.map((s, i) => (
              <StageRow key={s.id} stage={s} kinds={kinds} index={i} total={stages.length}
                onSaved={saveStage} onReorder={reorder} onArchive={archiveStage} />
            ))}
          </div>
          <div className="crm-stage-row" style={{ borderStyle: 'dashed' }}>
            <Icon name="plus" size={16} className="text-mute" />
            <div className="crm-stage-row__name">
              <TextInput value={newStage} onChange={(e) => setNewStage(e.target.value)}
                placeholder="Новый этап" onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }} />
            </div>
            <Button size="sm" variant="secondary" loading={addingStage} disabled={!newStage.trim()} onClick={addStage}>Добавить</Button>
          </div>
        </div>
      )}

      {tab === 'pipeline' && (
        <div className="stack stack-12">
          <Field label="Название воронки"><TextInput value={pname} onChange={(e) => setPname(e.target.value)} /></Field>
          <Field label="Код (для API)" hint="Используется в поле pipeline при приёме заявок.">
            <TextInput value={pslug} onChange={(e) => setPslug(e.target.value)} />
          </Field>
          <label className="cluster cluster-8" style={{ alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={pdefault} onChange={(e) => setPdefault(e.target.checked)} />
            <span>Воронка по умолчанию (сюда падают заявки из API без указания воронки)</span>
          </label>
          <div className="cluster cluster-8">
            <Button variant="primary" loading={savingP} onClick={savePipeline}>Сохранить</Button>
            <div className="grow" />
            <Button variant="ghost" iconLeft="trash" onClick={archivePipeline}>Архивировать воронку</Button>
          </div>
        </div>
      )}

      {tab === 'api' && (
        <div className="stack stack-12">
          <p className="text-dim">Внешние источники шлют заявки на единый endpoint; воронку выбирает поле <code>pipeline</code> (код воронки).</p>
          <Field label="Статус приёма">
            <span className={'text-' + (ingest.configured ? 'dim' : 'mute')}>
              {ingest.configured ? '✓ Настроен (ключ задан в окружении сервера)' : 'Не настроен — задайте CRM_INGEST_API_KEY в окружении'}
            </span>
          </Field>
          <Field label="Пример запроса"><div className="crm-codeblock">{example}</div></Field>
          <div className="cluster cluster-8">
            <Button size="sm" variant="secondary" iconLeft="copy" onClick={() => copy(example)}>Скопировать пример</Button>
            {ingest.key && <Button size="sm" variant="ghost" iconLeft="copy" onClick={() => copy(ingest.key)}>Скопировать ключ</Button>}
          </div>
          <p className="t-caption text-mute">Повтор с тем же <code>external_id</code> в этой воронке не создаёт дубль.</p>
        </div>
      )}
    </div>
  );
}

function CrmSettings() {
  const navigate = useNavigate();
  const { pipelineId } = useParams();
  const toast = useToast();
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const pipesQ = useFakeQuery('crm-settings-pipes-' + nonce, () => crm.listPipelines(), [nonce]);
  const metaQ = useFakeQuery('crm-settings-meta', () => crm.getCrmMeta(), []);
  const pipelines = pipesQ.data || [];
  const meta = metaQ.data || null;

  const selectedId = pipelineId ? Number(pipelineId) : (pipelines[0] ? pipelines[0].id : null);
  const pipeline = useMemo(() => pipelines.find((p) => p.id === selectedId) || null, [pipelines, selectedId]);

  const [npName, setNpName] = useState('');
  const [creating, setCreating] = useState(false);
  const createPipeline = async () => {
    if (!npName.trim()) return;
    setCreating(true);
    try {
      const p = await crm.createPipeline({ name: npName.trim() });
      setNpName(''); reload(); navigate('/console/crm/settings/' + p.id);
      toast.show('Воронка создана', 'accent');
    } catch (e) { toast.show(e?.detail || 'Не удалось создать воронку.', 'danger'); }
    finally { setCreating(false); }
  };

  const actions = (
    <>
      <Button variant="ghost" iconLeft="arrow-left" onClick={() => navigate('/console')}>Сервисы</Button>
      <Button variant="secondary" iconLeft="layers" onClick={() => navigate('/console/crm')}>К доске</Button>
    </>
  );

  if ((pipesQ.loading && !pipelines.length) || (metaQ.loading && !meta)) {
    return (
      <div className="section-page">
        <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'CRM', to: '/console/crm' }, { label: 'Настройка' }]} title="Настройка воронок" actions={actions} />
        <div className="section-body"><div className="state state--loading"><Spinner size="lg" label="Загрузка…" /></div></div>
      </div>
    );
  }

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'CRM', to: '/console/crm' }, { label: 'Настройка' }]} title="Настройка воронок" actions={actions} />
      <div className="section-body">
        {pipesQ.error ? (
          <ErrorState title="Не удалось загрузить воронки" onRetry={pipesQ.reload} />
        ) : (
          <div className="crm-settings">
            <div className="crm-settings__list">
              <div className="sidebar__section-title">Воронки</div>
              {pipelines.map((p) => (
                <button key={p.id} type="button"
                  className={'crm-funnel-row' + (p.id === selectedId ? ' is-active' : '')}
                  onClick={() => navigate('/console/crm/settings/' + p.id)}>
                  <span className="crm-funnel-row__name truncate">{p.name}</span>
                  {p.is_default && <Pill tone="accent" size="sm">по умолч.</Pill>}
                  <span className="crm-col__count num">{p.lead_count}</span>
                </button>
              ))}
              <div className="crm-funnel-row" style={{ borderStyle: 'dashed' }}>
                <Icon name="plus" size={15} className="text-mute" />
                <div className="grow" style={{ minWidth: 0 }}>
                  <TextInput value={npName} onChange={(e) => setNpName(e.target.value)} placeholder="Новая воронка"
                    onKeyDown={(e) => { if (e.key === 'Enter') createPipeline(); }} />
                </div>
                <Button size="sm" variant="secondary" loading={creating} disabled={!npName.trim()} onClick={createPipeline}>+</Button>
              </div>
            </div>

            {pipeline ? (
              <FunnelDetail
                key={pipeline.id}
                pipeline={pipeline}
                meta={meta}
                onChanged={reload}
                onArchived={() => { reload(); navigate('/console/crm/settings'); }}
              />
            ) : (
              <EmptyState icon="layers" title="Нет воронок"
                body="Создайте первую воронку слева, чтобы настроить её этапы и приём заявок." />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { CrmSettings };
